/**
 * Needs attention (#92): a plain-English list of competitions that need a
 * human to do something, derived entirely from `deriveLifecycle` — never
 * from stored state. Used by `CompetitionAttentionPanel` on the admin home
 * and in the competitions list.
 *
 * Three rules, independent of each other (an event can appear once per rule
 * that applies, though in practice at most one applies at a time):
 *   - ended, not adjudicated, not cancelled, more than 3 days after the end;
 *   - adjudicated, not published, more than 7 days after adjudicatedAt;
 *   - fishing starts within 7 days and the times are incomplete.
 * Inactive (`isActive` false) and cancelled events are ignored entirely.
 */
import { deriveLifecycle, type CompetitionFacts } from './competitionRules';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Minimum event shape the attention rules need: lifecycle facts plus id/title and isActive. */
export interface AttentionEvent extends CompetitionFacts {
  id: string;
  title?: string | null;
  isActive?: boolean | null;
}

export interface AttentionItem {
  eventId: string;
  title: string;
  /** Plain English, for example "Ended 5 days ago and not yet adjudicated." */
  message: string;
}

const DAYS_AFTER_END_BEFORE_ADJUDICATION_FLAG = 3;
const DAYS_AFTER_ADJUDICATION_BEFORE_PUBLISH_FLAG = 7;
const DAYS_BEFORE_START_FOR_TIMING_FLAG = 7;

function daysSince(from: Date, now: Date): number {
  return (now.getTime() - from.getTime()) / DAY_MS;
}

function titleOf(e: AttentionEvent): string {
  return e.title || 'Untitled competition';
}

/**
 * Lists competitions needing attention, in plain English, newest-ended
 * (or soonest-starting) first within each rule. `now` is injected so the
 * boundary is testable; callers pass `new Date()`.
 */
export function getCompetitionAttentionItems(events: AttentionEvent[], now: Date): AttentionItem[] {
  const items: AttentionItem[] = [];

  for (const event of events) {
    if (event.isActive === false) continue;
    const lifecycle = deriveLifecycle(event, now);
    if (lifecycle.cancelled) continue;

    const title = titleOf(event);

    // Ended, not adjudicated, more than 3 days after the end.
    if (lifecycle.ended && !lifecycle.adjudicated && lifecycle.catchDeadline) {
      const days = daysSince(lifecycle.catchDeadline, now);
      if (days > DAYS_AFTER_END_BEFORE_ADJUDICATION_FLAG) {
        items.push({
          eventId: event.id,
          title,
          message: `${title}: ended ${Math.floor(days)} days ago and still not adjudicated.`,
        });
      }
    }

    // Adjudicated, not published, more than 7 days after adjudicatedAt.
    if (lifecycle.adjudicated && !lifecycle.published && event.adjudicatedAt) {
      const adjudicatedAt = new Date(event.adjudicatedAt);
      if (!Number.isNaN(adjudicatedAt.getTime())) {
        const days = daysSince(adjudicatedAt, now);
        if (days > DAYS_AFTER_ADJUDICATION_BEFORE_PUBLISH_FLAG) {
          items.push({
            eventId: event.id,
            title,
            message: `${title}: adjudicated ${Math.floor(days)} days ago and still not published.`,
          });
        }
      }
    }

    // Fishing starts within 7 days with incomplete times.
    if (lifecycle.catchReason === 'TIMING_INCOMPLETE' && event.eventDate) {
      const eventDate = new Date(event.eventDate);
      if (!Number.isNaN(eventDate.getTime())) {
        const daysUntilStart = (eventDate.getTime() - now.getTime()) / DAY_MS;
        if (daysUntilStart >= 0 && daysUntilStart <= DAYS_BEFORE_START_FOR_TIMING_FLAG) {
          items.push({
            eventId: event.id,
            title,
            message: `${title}: fishing starts soon but the start or end time is not set.`,
          });
        } else if (!lifecycle.adjudicated && -daysUntilStart > DAYS_AFTER_END_BEFORE_ADJUDICATION_FLAG) {
          // #134 review: a past competition with no times never "ends", so the
          // ended rule above cannot catch it. Flag it once its day has passed.
          items.push({
            eventId: event.id,
            title,
            message: `${title}: the day has passed, the start or end time was never set, and it is not adjudicated.`,
          });
        }
      }
    }
  }

  return items;
}
